import { useMemo, useState } from 'react';
import {
  Activity, ChevronDown, Clock, Download, Laptop, LogIn, MoonStar, Users,
} from 'lucide-react';
import {
  USAGE_AREA_LABEL, type UsageArea,
} from '@pointage/shared';
import {
  useUsageOverview, useUsageSessions,
  type UsageScope, type UsageSessionDTO, type UsageUserRow,
} from '../../api/hooks.js';
import { ROLE_LABEL } from './AccessCard.js';

// Connexions & usage — who signs in, from where, and how long the app
// is actually in front of them. Owner screen (`usage.view`, BOSS only
// by default), so it reports on people: every number here is one a
// colleague could be asked about, which is why the copy states what is
// measured and what is deliberately not.
//
// "Temps actif" is NOT session length. The server only counts the gaps
// between two signs of life while they stay under five minutes, so a
// tab left open all afternoon reads as the twenty minutes it was
// really used. That distinction is spelled out on screen — a boss
// reading "6 h" must not think it means six hours at the keyboard.

// ─── formatting ──────────────────────────────────────────────
const pad = (n: number) => String(n).padStart(2, '0');

/** 5 400 → "1 h 30". Short form for tiles and table cells. */
function fmtDur(seconds: number): string {
  if (seconds <= 0) return '—';
  const m = Math.round(seconds / 60);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const rest = m % 60;
  return rest === 0 ? `${h} h` : `${h} h ${pad(rest)}`;
}

const DATE_FMT = new Intl.DateTimeFormat('fr-FR', { day: '2-digit', month: 'short' });
const DATETIME_FMT = new Intl.DateTimeFormat('fr-FR', {
  day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
});
const TIME_FMT = new Intl.DateTimeFormat('fr-FR', { hour: '2-digit', minute: '2-digit' });

const dayLabel = (iso: string) => DATE_FMT.format(new Date(`${iso}T12:00:00Z`));

/** "il y a 3 j" / "il y a 12 min" — relative, French, no library. */
function since(iso: string | null): string {
  if (!iso) return 'jamais';
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.round(ms / 60_000);
  if (min < 1) return "à l'instant";
  if (min < 60) return `il y a ${min} min`;
  const h = Math.round(min / 60);
  if (h < 24) return `il y a ${h} h`;
  const d = Math.round(h / 24);
  if (d < 31) return `il y a ${d} j`;
  const mo = Math.round(d / 30);
  return `il y a ${mo} mois`;
}

const initials = (name: string) =>
  name.trim().split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]?.toUpperCase() ?? '').join('') || '?';

const AVATAR_COLORS = ['var(--shf)', 'var(--sgn)', 'var(--oct)', 'var(--sga)'];
const avColor = (id: string) => {
  let h = 0;
  for (const ch of id) h = (h * 31 + ch.charCodeAt(0)) % 997;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
};

const isoDaysAgo = (n: number) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

// ─── période presets ─────────────────────────────────────────
const PRESETS = [
  { id: '7', label: '7 jours', days: 6 },
  { id: '30', label: '30 jours', days: 29 },
  { id: '90', label: '90 jours', days: 89 },
] as const;
type PresetId = (typeof PRESETS)[number]['id'] | 'custom';

const SCOPES: Array<{ id: UsageScope; label: string }> = [
  { id: 'staff', label: 'Équipe' },
  { id: 'employee', label: 'Espace employé' },
  { id: 'all', label: 'Tous' },
];

type SortKey = 'activeSeconds' | 'sessions' | 'lastSeenAt' | 'name';

// ═══════════════════════════════════════════════════════════════
export function UsageCard() {
  const [preset, setPreset] = useState<PresetId>('30');
  const [from, setFrom] = useState(isoDaysAgo(29));
  const [to, setTo] = useState(isoDaysAgo(0));
  const [scope, setScope] = useState<UsageScope>('staff');
  const [sort, setSort] = useState<SortKey>('activeSeconds');
  const [openUser, setOpenUser] = useState<string | null>(null);

  const applyPreset = (id: PresetId) => {
    setPreset(id);
    const p = PRESETS.find((x) => x.id === id);
    if (p) { setFrom(isoDaysAgo(p.days)); setTo(isoDaysAgo(0)); }
  };

  const filters = useMemo(() => ({ from, to, scope }), [from, to, scope]);
  const overview = useUsageOverview(filters);
  const data = overview.data;

  const rows = useMemo(() => {
    const list = [...(data?.users ?? [])];
    list.sort((a, b) => {
      switch (sort) {
        case 'sessions': return b.sessions - a.sessions;
        case 'name': return a.name.localeCompare(b.name, 'fr');
        case 'lastSeenAt':
          return (b.lastSeenAt ?? b.lastLoginAt ?? '').localeCompare(a.lastSeenAt ?? a.lastLoginAt ?? '');
        default: return b.activeSeconds - a.activeSeconds;
      }
    });
    return list;
  }, [data, sort]);

  const seen = rows.filter((u) => u.sessions > 0);
  const dormant = rows.filter((u) => u.sessions === 0 && u.accountActive);

  const exportCsv = () => {
    const params = new URLSearchParams({ format: 'csv', from, to, scope, limit: '2000' });
    window.open(`/api/usage/sessions?${params.toString()}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="scard-body" data-testid="usage-card">
      {/* ─── filtres ─────────────────────────────────────── */}
      <div className="usg-toolbar">
        <div className="usg-seg" role="group" aria-label="Période">
          {PRESETS.map((p) => (
            <button key={p.id} className={'usg-seg-btn' + (preset === p.id ? ' on' : '')}
              onClick={() => applyPreset(p.id)} data-testid={`usg-preset-${p.id}`}>
              {p.label}
            </button>
          ))}
        </div>
        <input className="set-input" type="date" value={from} aria-label="Du"
          onChange={(e) => { setFrom(e.target.value); setPreset('custom'); }} data-testid="usg-from" />
        <span className="usg-dash">→</span>
        <input className="set-input" type="date" value={to} aria-label="Au"
          onChange={(e) => { setTo(e.target.value); setPreset('custom'); }} data-testid="usg-to" />

        <div className="usg-seg" role="group" aria-label="Périmètre">
          {SCOPES.map((s) => (
            <button key={s.id} className={'usg-seg-btn' + (scope === s.id ? ' on' : '')}
              onClick={() => setScope(s.id)} data-testid={`usg-scope-${s.id}`}>
              {s.label}
            </button>
          ))}
        </div>

        <div className="toolbar-spacer" />
        <button className="btn btn-sm" onClick={exportCsv} data-testid="usg-csv">
          <Download aria-hidden="true" /> Exporter
        </button>
      </div>

      {overview.isLoading && <p className="srow-help" style={{ padding: 16 }}>Chargement…</p>}
      {overview.isError && (
        <p className="srow-help" style={{ padding: 16 }} data-testid="usg-error">
          Impossible de charger les statistiques d’usage.
        </p>
      )}

      {data && (
        <>
          {/* ─── KPI ───────────────────────────────────────── */}
          <div className="usg-kpis">
            <Kpi Icon={Users} label="Actifs sur la période"
              value={`${data.totals.activeUsers}`}
              hint={`sur ${data.totals.accounts} compte${data.totals.accounts > 1 ? 's' : ''}`}
              testid="usg-kpi-active" />
            <Kpi Icon={Clock} label="Temps actif cumulé"
              value={fmtDur(data.totals.activeSeconds)}
              hint={`≈ ${fmtDur(data.totals.avgPerUserSeconds)} par personne active`}
              testid="usg-kpi-time" />
            <Kpi Icon={LogIn} label="Connexions"
              value={`${data.totals.sessions}`}
              hint={`durée moyenne ${fmtDur(data.totals.avgSessionSeconds)}`}
              testid="usg-kpi-sessions" />
            <Kpi Icon={Activity} label="En ligne maintenant"
              value={`${data.totals.onlineNow}`}
              hint={data.totals.onlineNow > 0 ? 'actifs dans les 5 dernières minutes' : 'personne connectée'}
              live={data.totals.onlineNow > 0}
              testid="usg-kpi-online" />
          </div>

          {/* ─── en ligne ──────────────────────────────────── */}
          {data.online.length > 0 && (
            <div className="usg-live" data-testid="usg-live">
              <div className="usg-live-head"><span className="usg-dot" /> En ligne maintenant</div>
              <div className="usg-live-list">
                {data.online.map((o) => (
                  <div className="usg-live-chip" key={o.sessionId}>
                    <span className="usg-av sm" style={{ background: avColor(o.userId) }}>{initials(o.name)}</span>
                    <div>
                      <div className="usg-live-name">{o.name}</div>
                      <div className="usg-live-meta">
                        {o.area ? USAGE_AREA_LABEL[o.area] : 'Navigation'} · depuis {TIME_FMT.format(new Date(o.since))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ─── courbe quotidienne ────────────────────────── */}
          <DailyChart daily={data.daily} />

          {/* ─── par personne ──────────────────────────────── */}
          <div className="usg-sec-head">
            <h3>Par personne</h3>
            <div className="usg-sorts">
              <span className="usg-sort-lbl">Trier&nbsp;:</span>
              {([
                ['activeSeconds', 'Temps actif'],
                ['sessions', 'Connexions'],
                ['lastSeenAt', 'Dernier accès'],
                ['name', 'Nom'],
              ] as Array<[SortKey, string]>).map(([k, lbl]) => (
                <button key={k} className={'usg-sort' + (sort === k ? ' on' : '')}
                  onClick={() => setSort(k)} data-testid={`usg-sort-${k}`}>{lbl}</button>
              ))}
            </div>
          </div>

          <div className="usg-table-wrap">
            <table className="usg-table" data-testid="usg-table">
              <thead>
                <tr>
                  <th>Personne</th>
                  <th className="num">Temps actif</th>
                  <th className="hide-sm">Activité</th>
                  <th className="num">Connexions</th>
                  <th className="num hide-sm">Durée moy.</th>
                  <th className="num hide-sm">Jours actifs</th>
                  <th className="hide-md">Module principal</th>
                  <th>Dernier accès</th>
                  <th aria-label="Détail" />
                </tr>
              </thead>
              <tbody>
                {seen.map((u) => (
                  <UserRow key={u.id} u={u} days={data.range.days}
                    open={openUser === u.id}
                    onToggle={() => setOpenUser(openUser === u.id ? null : u.id)}
                    filters={filters} />
                ))}
                {seen.length === 0 && (
                  <tr><td colSpan={9} className="usg-empty" data-testid="usg-empty">
                    Aucune connexion sur la période.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* ─── répartition par module ────────────────────── */}
          {data.areas.length > 0 && (
            <>
              <div className="usg-sec-head"><h3>Où passe le temps</h3></div>
              <div className="usg-areas" data-testid="usg-areas">
                {data.areas.slice(0, 10).map((a) => (
                  <div className="usg-area" key={a.area}>
                    <div className="usg-area-lbl">{USAGE_AREA_LABEL[a.area]}</div>
                    <div className="usg-area-track">
                      <div className="usg-area-fill" style={{ width: `${Math.max(2, a.share * 100)}%` }} />
                    </div>
                    <div className="usg-area-val">{fmtDur(a.activeSeconds)}</div>
                    <div className="usg-area-pct">{Math.round(a.share * 100)} %</div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* ─── comptes dormants ──────────────────────────── */}
          {dormant.length > 0 && (
            <>
              <div className="usg-sec-head">
                <h3><MoonStar aria-hidden="true" /> Comptes sans connexion sur la période</h3>
              </div>
              <div className="usg-dormant" data-testid="usg-dormant">
                {dormant.map((u) => (
                  <div className="usg-dorm-chip" key={u.id}>
                    <span className="usg-av sm" style={{ background: avColor(u.id) }}>{initials(u.name)}</span>
                    <div>
                      <div className="usg-live-name">{u.name}</div>
                      <div className="usg-live-meta">
                        {ROLE_LABEL[u.role]} · {u.lastLoginAt ? `dernière connexion ${since(u.lastLoginAt)}` : 'jamais connecté'}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          <p className="srow-help" style={{ padding: '14px 6px 0' }}>
            <b>Temps actif</b> ≠ durée de connexion. L’app envoie un signal chaque minute tant que l’onglet est
            visible ; seuls les intervalles de moins de 5 minutes sont comptés. Un onglet resté ouvert toute
            l’après-midi ne compte donc que le temps réellement passé dessus. Les pages ouvertes en arrière-plan,
            les contenus consultés et les frappes clavier ne sont pas enregistrés — uniquement le module affiché.
            Historique conservé 12 mois, puis purgé automatiquement.
            {data.truncated && ' Période très large : la liste des connexions a été tronquée, resserrez les dates.'}
          </p>
        </>
      )}
    </div>
  );
}

// ─── KPI tile ────────────────────────────────────────────────
function Kpi({ Icon, label, value, hint, live, testid }: {
  Icon: typeof Users; label: string; value: string; hint: string; live?: boolean; testid: string;
}) {
  return (
    <div className={'usg-kpi' + (live ? ' live' : '')} data-testid={testid}>
      <div className="usg-kpi-ico"><Icon aria-hidden="true" /></div>
      <div className="usg-kpi-lbl">{label}</div>
      <div className="usg-kpi-val">{value}</div>
      <div className="usg-kpi-hint">{hint}</div>
    </div>
  );
}

// ─── daily bars ──────────────────────────────────────────────
function DailyChart({ daily }: { daily: Array<{ date: string; activeSeconds: number; sessions: number; users: number }> }) {
  const max = Math.max(1, ...daily.map((d) => d.activeSeconds));
  // Above ~45 bars the labels stop being readable — show every nth.
  const step = daily.length > 45 ? Math.ceil(daily.length / 12) : daily.length > 14 ? Math.ceil(daily.length / 10) : 1;
  return (
    <div className="usg-chart" data-testid="usg-chart">
      <div className="usg-chart-head">
        <h3>Usage quotidien</h3>
        <span className="usg-chart-max">pic {fmtDur(max)}</span>
      </div>
      <div className="usg-bars">
        {daily.map((d, i) => (
          <div className="usg-bar-slot" key={d.date}
            title={`${dayLabel(d.date)} · ${fmtDur(d.activeSeconds)} · ${d.users} personne${d.users > 1 ? 's' : ''} · ${d.sessions} connexion${d.sessions > 1 ? 's' : ''}`}>
            <div className="usg-bar" style={{ height: `${(d.activeSeconds / max) * 100}%` }} />
            <span className="usg-bar-lbl">{i % step === 0 ? dayLabel(d.date) : ''}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── sparkline ───────────────────────────────────────────────
function Spark({ values }: { values: number[] }) {
  const max = Math.max(1, ...values);
  const w = 96;
  const h = 22;
  const bw = values.length > 0 ? w / values.length : w;
  return (
    <svg className="usg-spark" viewBox={`0 0 ${w} ${h}`} width={w} height={h} aria-hidden="true">
      {values.map((v, i) => {
        const bh = v > 0 ? Math.max(1.5, (v / max) * h) : 0;
        return <rect key={i} x={i * bw} y={h - bh} width={Math.max(1, bw - 0.8)} height={bh} rx={0.8} />;
      })}
    </svg>
  );
}

// ─── one person, expandable to their sessions ────────────────
function UserRow({ u, days, open, onToggle, filters }: {
  u: UsageUserRow;
  days: string[];
  open: boolean;
  onToggle: () => void;
  filters: { from: string; to: string; scope: UsageScope };
}) {
  const sessions = useUsageSessions({ ...filters, userId: u.id, limit: 100 }, open);
  return (
    <>
      <tr className={'usg-row' + (open ? ' open' : '')} data-testid={`usg-row-${u.id}`}>
        <td>
          <div className="usg-person">
            <span className="usg-av" style={{ background: avColor(u.id) }}>
              {initials(u.name)}
              {u.online && <span className="usg-av-dot" />}
            </span>
            <div className="usg-person-txt">
              <div className="usg-person-name">
                {u.name}
                {!u.accountActive && <span className="usg-tag off">désactivé</span>}
              </div>
              <div className="usg-person-sub">
                {ROLE_LABEL[u.role]}
                {u.online && u.currentArea ? ` · sur ${USAGE_AREA_LABEL[u.currentArea]}` : ''}
              </div>
            </div>
          </div>
        </td>
        <td className="num strong">{fmtDur(u.activeSeconds)}</td>
        <td className="hide-sm"><Spark values={u.daily} /></td>
        <td className="num">{u.sessions}</td>
        <td className="num hide-sm">{fmtDur(u.avgSessionSeconds)}</td>
        <td className="num hide-sm">{u.daysActive}<span className="usg-of"> / {days.length}</span></td>
        <td className="hide-md">{u.topArea ? USAGE_AREA_LABEL[u.topArea] : '—'}</td>
        <td>
          <span className={u.online ? 'usg-now' : ''}>{u.online ? 'en ligne' : since(u.lastSeenAt)}</span>
        </td>
        <td className="usg-td-btn">
          <button className={'usg-exp' + (open ? ' open' : '')} onClick={onToggle}
            aria-expanded={open} aria-label={`Connexions de ${u.name}`} data-testid={`usg-exp-${u.id}`}>
            <ChevronDown aria-hidden="true" />
          </button>
        </td>
      </tr>
      {open && (
        <tr className="usg-detail-row">
          <td colSpan={9}>
            <div className="usg-detail" data-testid={`usg-detail-${u.id}`}>
              {sessions.isLoading && <div className="usg-detail-empty">Chargement…</div>}
              {sessions.data && sessions.data.entries.length === 0 && (
                <div className="usg-detail-empty">Aucune connexion sur la période.</div>
              )}
              {sessions.data && sessions.data.entries.length > 0 && (
                <div className="usg-sessions">
                  {sessions.data.entries.map((s) => <SessionLine key={s.id} s={s} />)}
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function SessionLine({ s }: { s: UsageSessionDTO }) {
  const end = s.open ? 'en cours' : s.endedAt ? TIME_FMT.format(new Date(s.endedAt)) : '—';
  return (
    <div className={'usg-sess' + (s.open ? ' open' : '')}>
      <div className="usg-sess-when">
        <b>{DATETIME_FMT.format(new Date(s.startedAt))}</b>
        <span className="usg-sess-arrow">→</span>
        <span>{end}</span>
      </div>
      <div className="usg-sess-time">
        <b>{fmtDur(s.activeSeconds)}</b> actif
        <span className="usg-sess-span"> · fenêtre {fmtDur(s.spanSeconds)}</span>
      </div>
      <div className="usg-sess-area">{s.topArea ? USAGE_AREA_LABEL[s.topArea] : '—'}</div>
      <div className="usg-sess-dev">
        <Laptop aria-hidden="true" />
        {s.device ?? 'appareil inconnu'}
        {s.method === 'dev' && <span className="usg-tag">dev</span>}
      </div>
      <div className="usg-sess-ip mono">{s.ip ?? ''}</div>
    </div>
  );
}
